import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Board } from "../components/Board";
import { CoachPlan } from "../components/CoachPlan";
import { Feed } from "../components/Feed";
import { Levels } from "../components/Pips";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLoad } from "../lib/hooks";
import type { Home as HomeData, PlayRequest } from "../lib/types";

export function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { data, error } = useLoad(() => api<HomeData>("/home"));
  const [len, setLen] = useState(20);
  const play = (req: PlayRequest) => nav("/play", { state: req });
  if (error) return <main className="page"><div className="error">{error}</div></main>;
  if (!data) return <main className="page"><div className="empty">Loading…</div></main>;
  const d = data.daily, wk = data.week;

  return <main className="page">
    <div className="card-head" style={{ marginTop: 8 }}>
      <h1 style={{ margin: 0 }}>Hi, {user!.displayName}</h1>
      <span className="mute">{data.streak.current ? `${data.streak.current}-day streak` : "Start a streak today"}</span>
    </div>

    {data.challenges.toPlay.length > 0 && <div className="card" style={{ marginTop: 16, boxShadow: "inset 4px 0 0 var(--c-accent)" }}>
      <div className="card-title">Your move</div>
      {data.challenges.toPlay.slice(0, 3).map((c) => <div key={c.id} className="card-head" style={{ marginTop: 8 }}>
        <span>{c.opponent.displayName} challenged you{c.message ? `: “${c.message}”` : ""}</span>
        <button className="btn ink" style={{ padding: "8px 14px" }} onClick={() => play({ mode: "challenge", challengeId: c.id })}>Play</button>
      </div>)}
    </div>}

    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head"><span className="card-title">Your coach</span><Link to="/grow" className="small">More</Link></div>
      <p style={{ margin: "0 0 8px" }}>{data.growth.coach.headline}</p>
      <CoachPlan plan={data.growth.coach.plan} />
      {data.growth.goals.map((g) => <div key={g.id} style={{ marginTop: 10 }}>
        <div className="small">{g.title}</div>
        <div className="meter"><i style={{ width: `${g.fraction * 100}%` }} /></div>
      </div>)}
    </div>

    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head"><span className="card-title">Today's challenge</span><span className="small mute">{d.players} played</span></div>
      {d.played && d.mine
        ? <p style={{ margin: "4px 0 10px" }}>You scored <b>{d.mine.value}</b> and you're <b>#{d.mine.rank}</b> of {d.players}.</p>
        : <p className="mute" style={{ margin: "4px 0 10px" }}>{d.length} problems, the same skills for everyone and set to your level. One try a day, and your score rewards accuracy and speed rather than rank.</p>}
      {d.top.length > 0 && <Board rows={d.top} />}
      {!d.played && <button className="btn-primary" style={{ marginTop: 10 }} onClick={() => play({ mode: "daily" })}>Play today's challenge</button>}
      {d.played && <Link className="btn quiet" to="/ranks?board=daily">See today's ranks</Link>}
    </div>

    <h2>Practice</h2>
    <div className="seg" role="group" aria-label="Session length" style={{ marginBottom: 10 }}>
      {[10, 20, 40].map((n) => <button key={n} aria-pressed={len === n} onClick={() => setLen(n)}>{n}</button>)}
    </div>
    <button className="btn-primary" onClick={() => play({ mode: "adaptive", length: len })}>Start {len} problems</button>
    <div className="grid2" style={{ marginTop: 10 }}>
      <Link className="btn" to="/focus" state={{ length: len }}>Focus<small>pick skills</small></Link>
      <button className="btn" disabled={!data.reviewsDue} onClick={() => play({ mode: "review" })}>Review<small>{data.reviewsDue ? `${data.reviewsDue} due` : "nothing due"}</small></button>
    </div>

    <h2>This week</h2>
    <div className="card">
      <div className="card-head"><span className="mute">{wk.value && wk.rank ? `You're #${wk.rank} of ${wk.of}` : "No points yet this week"}</span><span className="big-num">{wk.value}</span></div>
      <Board rows={wk.top} />
      <Link className="btn quiet" to="/ranks">All leaderboards</Link>
    </div>

    <h2>Your levels</h2>
    <div className="card"><Levels ratings={data.ratings} />
      {data.nextUnlock && <p className="small mute" style={{ margin: "10px 0 0" }}>
        {data.nextUnlock.skills.join(" and ")} open up as your basics (addition, subtraction, multiplication, division) average level {data.nextUnlock.at}. You're at {data.nextUnlock.progress.toFixed(1)}. You can still pick any skill in Focus.
      </p>}
    </div>

    <h2>Activity</h2>
    <Feed events={data.feed} />
  </main>;
}
